import { Link } from "react-router-dom";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Badge,
	Button,
} from "@/components/primitives";
import { Edit, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { formatRelativeDate } from "@/lib/utils";
import type { Reflection } from "../types";

interface ReflectionCardProps {
	reflection: Reflection;
	onDelete: (id: string) => void;
	onArchive?: (id: string) => void;
	onUnarchive?: (id: string) => void;
}

export const ReflectionCard = ({
	reflection,
	onDelete,
	onArchive,
	onUnarchive,
}: ReflectionCardProps) => {
	return (
		<Card className="border-2 shadow-md hover:shadow-xl transition-all duration-200 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
			<CardHeader className="pb-3">
				<div className="flex items-start justify-between">
					<CardTitle className="text-lg font-semibold line-clamp-2">
						{reflection.title || "Untitled Reflection"}
					</CardTitle>
					<div className="flex items-center space-x-2 ml-2">
						{reflection.is_draft && (
							<Badge variant="outline" className="text-xs">
								Draft
							</Badge>
						)}
						{reflection.archived && (
							<Badge
								variant="outline"
								className="text-xs text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20"
							>
								Archived
							</Badge>
						)}
					</div>
				</div>
				<CardDescription className="line-clamp-3">
					{reflection.description || "No description available"}
				</CardDescription>
			</CardHeader>
			<CardContent className="pt-0">
				<div className="flex flex-wrap gap-1 mb-4">
					{(Array.isArray(reflection.topic_tags)
						? reflection.topic_tags
								.filter(
									(tag) =>
										!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
											tag,
										),
								)
								.slice(0, 3)
						: []
					).map((tag) => (
						<Badge key={tag} variant="secondary" className="text-xs">
							{tag}
						</Badge>
					))}
					{Array.isArray(reflection.topic_tags) &&
						reflection.topic_tags.filter(
							(tag) =>
								!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
									tag,
								),
						).length > 3 && (
							<Badge variant="secondary" className="text-xs">
								+
								{reflection.topic_tags.filter(
									(tag) =>
										!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
											tag,
										),
								).length - 3}{" "}
								more
							</Badge>
						)}
				</div>

				<div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
					<span>
						{reflection.is_draft ? "Updated" : "Created"}{" "}
						{formatRelativeDate(reflection.updated_at)}
					</span>
				</div>

				<div className="flex items-center justify-between">
					{reflection.is_draft ? (
						<Link
							to={`/reflection/continue/${reflection.id}`}
							className="flex-1"
						>
							<Button size="sm" className="w-full">
								<Edit className="h-3 w-3 mr-1" />
								Continue
							</Button>
						</Link>
					) : (
						<Link to={`/reflection/${reflection.id}`} className="flex-1">
							<Button size="sm" variant="outline" className="w-full">
								<Edit className="h-3 w-3 mr-1" />
								View Details
							</Button>
						</Link>
					)}
					{reflection.is_draft ? (
						<Button
							size="sm"
							variant="ghost"
							className="ml-2 text-muted-foreground hover:text-destructive"
							onClick={() => onDelete(reflection.id)}
						>
							<Trash2 className="h-3 w-3" />
						</Button>
					) : reflection.archived ? (
						<Button
							size="sm"
							variant="ghost"
							className="ml-2 text-muted-foreground hover:text-primary"
							onClick={() => onUnarchive && onUnarchive(reflection.id)}
							title="Unarchive"
						>
							<ArchiveRestore className="h-3 w-3" />
						</Button>
					) : (
						<Button
							size="sm"
							variant="ghost"
							className="ml-2 text-muted-foreground hover:text-primary"
							onClick={() => onArchive && onArchive(reflection.id)}
							title="Archive"
						>
							<Archive className="h-3 w-3" />
						</Button>
					)}
				</div>
			</CardContent>
		</Card>
	);
};
